import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AfterViewInit, Component, ViewChild, OnInit } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, firstValueFrom, lastValueFrom, skip, takeUntil } from 'rxjs';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { KeycloakUser } from 'src/app/shared/models/database-models/KeycloakUser.model';
import {
  ExtendedKeycloakUser,
  MyPurchaseForm,
  StoreDropdowns,
} from 'src/app/shared/models/app-models';
import { Location } from '@angular/common';
import { KeycloakADUser } from 'src/app/shared/models/database-models/KeycloakADUser';
import {
  AuditLogHeader,
  AuditLogHistory,
} from '../../../shared/models/database-models/AuditLog';
import { AuditChildRecord, AuditChildTable, auditChildColumns, testData } from './audit-log-child-record';
import { BaseTableColumn } from 'src/app/shared/models/app-models/BaseTableColumn.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateRangeValidator } from './date-range-validator';

export interface AuditLogHeaderData {
  auditData: AuditLogHeader[];
  totalCount: number;
}
export interface AuditLogHistoryData {
  historyData: AuditLogHistory[];
  totalCount: number;
}
@Component({
  selector: 'app-audit-log',
  templateUrl: './audit-log.component.html',
  styleUrls: ['./audit-log.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class AuditLogPageComponent implements AfterViewInit, OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  pageSizeOptions: number[] = [5, 10, 20];
  pageSizeHeader: number = 5;
  pageSizeHistory: number = 5;
  currentPageHeader: number = 0;
  currentPageHistory: number = 0;
  totalCountHeader: number = 0;
  totalCountHistory: number = 0;
  currentAuditHeaderId: string = '';
  currentRoute: string[] = ['My Worklist'];
  breadCrumbFlow:string[]=['Purchase Modes'];
  currentPageString: string = 'Audit Log';
  activePage:string='Audit Log';
  columnsToDisplay = ['number', 'modified-date-time', 'event', 'personnel'];
  columnsToDisplayHistory = ['attribute', 'before', 'after'];
  columnsToDisplayWithExpand = ['expand', ...this.columnsToDisplay];
  dataSourceHeader = new MatTableDataSource<AuditLogHeader>(ELEMENT_DATA_HEADER);
  dataSourceHistory = new MatTableDataSource<AuditLogHistory>(ELEMENT_DATA_HISTORY);
  expandedElements: Set<AuditLogHeader> = new Set();
  mpDocumber: string = '';
  form!: MyPurchaseForm;
  destroy$: Subject<boolean> = new Subject<boolean>();
  KCUserList: KeycloakADUser[] = [];
  testData: AuditChildTable[] = testData
  auditChildColumns: BaseTableColumn<AuditChildRecord>[] = auditChildColumns
  auditData: AuditLogHeader[] = [];
  //private readonly _currentYear = new Date().getFullYear();
  isLoadingData: boolean = false;
  readonly minDate = new Date(new Date().getFullYear() - 10, 0, 1);
  readonly maxDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59);
  readonly range = new FormGroup({
    start: new FormControl<Date>(new Date(new Date().setDate(new Date().getDate() - 6)), [DateRangeValidator(this.minDate, this.maxDate)]),
    end: new FormControl<Date>(new Date(), [DateRangeValidator(this.minDate, this.maxDate)]),
  });
  constructor(
    private route: ActivatedRoute,
    private backEndService: BackendApiService,
    private store: Store<{ form: MyPurchaseForm; dp: StoreDropdowns }>,
    private location: Location

  ) {
    const previousPageName = sessionStorage.getItem('fromPage');
    if (previousPageName) {
      this.breadCrumbFlow = [previousPageName];
    } 

    if (this.location.path().includes("rfqSummary")) {
      const idParam = this.route.snapshot.params['id'] as string;
      this.currentRoute.push(idParam);
      this.breadCrumbFlow = ["RFQ Summary", idParam];
      this.mpDocumber = idParam;
    }
    else if (this.route.snapshot.params['id']) {
      //from ao
      let docIdFromParam=this.route.snapshot.params['id'] as string;
      let purchaseTypeParam = this.route.snapshot.params['purchaseType'].split(
        ';'
      )[0] as string;
      this.currentRoute.push(docIdFromParam);
      if (docIdFromParam != 'draft') {
        this.breadCrumbFlow.push(this.route.snapshot.params['id'] as string);
      } else {
        switch (purchaseTypeParam) {
          case 'rfq':
            this.currentRoute.push('Request For Quotation');
            this.breadCrumbFlow.push('Request For Quotation');
            break;
          case 'tender':
            this.currentRoute.push('Tender');
            this.breadCrumbFlow.push('Tender');
            break;
          case 'corporatecard':
            this.currentRoute.push('Corporate Credit Card');
            this.breadCrumbFlow.push('Corporate Credit Card');
            break;
          case 'dc':
            this.currentRoute.push('Direct Contracting');
            this.breadCrumbFlow.push('Direct Contracting');
            break;
          case 'permissiblecoveringpurchaseorder':
            this.currentRoute.push('Permissible Reasons');
            this.breadCrumbFlow.push('Permissible Reasons');
            break;
        }
      }
      const mpDocumber = this.route.snapshot.params['id'].split(';')[0] as string;
      this.mpDocumber = mpDocumber;
    }

    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.KCUserList = dp.UserList as ExtendedKeycloakUser[];
      });
  }
  
  async ngOnInit(): Promise<void> {
    try {
      if (this.mpDocumber.length === 0) {
        this.store
          .select('form')
          .pipe(takeUntil(this.destroy$))
          .subscribe((data) => {
            this.form = data;
          });
        this.mpDocumber = this.form.documentNo as string;
      }
      if (this.KCUserList.length == 0) {
        this.KCUserList = (
          await firstValueFrom(this.store.select('dp').pipe(skip(1)))
        ).UserList as ExtendedKeycloakUser[];
      }
      await this.loadAuditHeader(this.mpDocumber);

    } catch (e) { }
  }

  async loadAuditHeader(mpDocNo: string, startDate?: Date, endDate?: Date) {
    this.isLoadingData = true
    try {
      if (!startDate && !endDate) {
        startDate = new Date()
        endDate = new Date()
        startDate.setDate(startDate.getDate() - 6)
      }
      let data: AuditLogHeaderData = await lastValueFrom(
        this.backEndService
          .getAuditLogHeader(
            mpDocNo,
            startDate,
            endDate
          )
          .pipe(takeUntil(this.destroy$))
      );
      
      for (const record of data.auditData) {
        record.CreatedBy = this.getFullNameOfUser(record.CreatedBy);
      }
      this.dataSourceHeader = data.auditData as any;
      this.totalCountHeader = data.totalCount;
      this.auditData = data.auditData;
    }
    catch (err: unknown) {
      console.error(err)
    }
    finally {
      this.isLoadingData = false
    }
  }

  getFullNameOfUser(userName: string) {
    if (userName && userName.length > 0) {
      let userResult = this.KCUserList.find(
        (user) => user.username == userName
      );
      if (userResult) return userResult.last_name;
      return userName;
    } else return userName;
  }
  ngAfterViewInit() {
    this.dataSourceHeader.paginator = this.paginator;
  }

  isExpanded(element: AuditLogHeader): boolean {
    return this.expandedElements.has(element);
  }
  // Expand a single element
  expand(element: AuditLogHeader): void {
    this.expandedElements.add(element);
  }
  // Collapse a single element
  collapse(element: AuditLogHeader): void {
    this.expandedElements.delete(element);
  }

  expandAll() {
    this.auditData.forEach(x => this.expandedElements.add(x));
  }

  collapseAll() {
    this.expandedElements = new Set()
  }

  returnToPreviousPage = (args: any): void => {
    this.location.back();
  };
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  async applyStartAndEndDates() {
    if (this.range.getRawValue().start == null || this.range.getRawValue().end == null) return
    if (this.range.controls.start.hasError("invalidDateRange") || this.range.controls.end.hasError("invalidDateRange")) {
      return;
    }
    await this.loadAuditHeader(this.mpDocumber, this.range.getRawValue().start!, this.range.getRawValue().end!)
  }
}

const ELEMENT_DATA_HEADER: AuditLogHeader[] = [
  {
    AuditId: '-',
    DocumentNo: '-',
    EventCode: '-',
    CreatedBy: '-',
    CreatedOn: null,
  },
];
const ELEMENT_DATA_HISTORY: AuditLogHistory[] = [
  {
    AuditHeaderId: '-',
    Entity: '-',
    FieldChanged: '-',
    ValueBefore: null,
    ValueAfter: '-',
    CreatedBy: '-',
    CreatedOn: null,
  },
];

